/**
 * PATH: packages/quote-builder/src/branding/defaults.ts
 * FILE_ID: QB-PKG-BRAND-DEFAULTS-001
 * PURPOSE: Tenant-NEUTRAL default quote brand + the shade helper the
 *          resolver uses to derive accentDark from a configured accent.
 * WHY: Convergence slice A2 — no surface may fall back to a hardcoded
 *      tenant brand. Orgs get their real brand from the backend payload
 *      (bootstrap / public quote view); this is what renders when an org
 *      has configured nothing. MUST stay in lockstep with the backend twin
 *      (quotebuilder/services/org_brand.py NEUTRAL_QUOTE_BRAND/shade_hex)
 *      so screen and server PDF derive identical colours.
 * FLOW:
 *   ┌────────────────┐   ┌──────────────────┐   ┌──────────────────────┐
 *   │ backend payload│ → │ brandFromSettings│ → │ QuoteBrand (falls    │
 *   │ (or none)      │   │ (+ shadeHex)     │   │ back to these tokens)│
 *   └────────────────┘   └──────────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - ./types: QuoteBrand shape (type-only, no runtime cycle)
 * RELATED:
 *   - quotebuilder/services/org_brand.py: backend twin — keep identical
 */

import type { QuoteBrand } from './types'

export const DEFAULT_LOGO_DISPLAY_HEIGHT_PX = 46
export const MIN_LOGO_DISPLAY_HEIGHT_PX = 24
export const MAX_LOGO_DISPLAY_HEIGHT_PX = 80

/** Letterhead-safe pixel height. Unparseable input falls back to 46. */
export function clampLogoDisplayHeightPx(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed)) return DEFAULT_LOGO_DISPLAY_HEIGHT_PX
  return Math.max(
    MIN_LOGO_DISPLAY_HEIGHT_PX,
    Math.min(MAX_LOGO_DISPLAY_HEIGHT_PX, Math.round(parsed)),
  )
}

/**
 * Neutral document brand. ink/muted/hairline/tint are the universal
 * document typography tokens every quote layout already used; accent /
 * secondary are deliberately quiet slates for unconfigured orgs.
 */
export const DEFAULT_QUOTE_BRAND: QuoteBrand = {
  companyName: '',
  abn: '',
  address: '',
  phone: '',
  tagline: '',
  accent: '#334155',
  accentDark: '#1e293b',
  secondary: '#64748b',
  ink: '#0f172a',
  muted: '#64748b',
  hairline: '#e2e8f0',
  tint: '#f1f5f9',
  logoUrl: null,
  logoDataUri: null,
  logoDisplayHeightPx: DEFAULT_LOGO_DISPLAY_HEIGHT_PX,
}

/**
 * Darken (#rrggbb, factor<1) or lighten (factor>1) a hex colour.
 * Unparseable input returns unchanged so a bad payload can never crash a
 * render. Math.round semantics — the backend twin mirrors this exactly.
 */
export function shadeHex(hexColor: string, factor: number): string {
  const raw = (hexColor || '').trim().replace(/^#/, '')
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return hexColor
  const shaded = [0, 2, 4]
    .map((i) => parseInt(raw.slice(i, i + 2), 16))
    .map((c) => Math.max(0, Math.min(255, Math.round(c * factor))))
  return `#${shaded.map((c) => c.toString(16).padStart(2, '0')).join('')}`
}

/** The factor accentDark is derived with when an org configures an accent. */
export const ACCENT_DARK_FACTOR = 0.82
